import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, X, Link2, RefreshCw, ChevronDown, ChevronUp, ExternalLink, AlertCircle } from 'lucide-react';
import { atlasApi, AtlasUser, Colaborador, DiscConvite, DiscConviteIgnorado, DiscConviteStatus } from '../shared/atlasApi';
import LinkConvite, { BadgeConvite, dataHora } from './LinkConvite';

interface Props {
  user: AtlasUser;
  onAbrirFicha: (funccodi: number) => void;
}

type Filtro = '' | Exclude<DiscConviteStatus, 'substituido'>;

const FILTROS: { id: Filtro; label: string }[] = [
  { id: '', label: 'Todas' },
  { id: 'pendente', label: 'Aguardando' },
  { id: 'respondido', label: 'Respondidas' },
  { id: 'expirado', label: 'Expiradas' },
  { id: 'cancelado', label: 'Canceladas' },
];

/** Busca de colaboradores ativos com seleção múltipla. */
const NovaSolicitacao: React.FC<{
  user: AtlasUser;
  onGerado: (itens: DiscConvite[], ignorados: DiscConviteIgnorado[]) => void;
  onFechar: () => void;
}> = ({ user, onGerado, onFechar }) => {
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<Colaborador[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [selecionados, setSelecionados] = useState<{ funccodi: number; nome: string }[]>([]);
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const termo = busca.trim();
    if (termo.length < 2) { setResultados([]); return; }
    let cancel = false;
    setBuscando(true);
    const t = window.setTimeout(() => {
      atlasApi.listarColaboradores(user, { busca: termo, status: 'ativo', per_page: 8 })
        .then((r: any) => { if (!cancel) setResultados(r?.itens || []); })
        .catch(() => { if (!cancel) setResultados([]); })
        .finally(() => { if (!cancel) setBuscando(false); });
    }, 300);
    return () => { cancel = true; window.clearTimeout(t); };
  }, [user, busca]);

  const adicionar = (c: Colaborador) => {
    if (selecionados.some(s => s.funccodi === c.funccodi)) return;
    setSelecionados(prev => [...prev, { funccodi: c.funccodi, nome: c.dados_publicos?.nome || `#${c.funccodi}` }]);
    setBusca('');
    setResultados([]);
  };

  const gerar = async () => {
    setGerando(true);
    setErro(null);
    try {
      const r = await atlasApi.discSolicitar(user, selecionados.map(s => s.funccodi));
      onGerado(r.itens, r.ignorados);
    } catch (e: any) {
      setErro(e?.message || 'Falha ao gerar os links.');
      setGerando(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-purple-200 p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Nova solicitação</h3>
        <button onClick={onFechar} className="p-1 rounded-md text-gray-400 hover:bg-gray-100"><X className="w-4 h-4" /></button>
      </div>
      <p className="text-sm text-gray-500">
        Escolha quem vai responder. Cada pessoa recebe um link próprio, que vale por 8 horas e serve para uma resposta.
      </p>

      {selecionados.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selecionados.map(s => (
            <span key={s.funccodi} className="pl-3 pr-1 py-1 rounded-full bg-purple-50 border border-purple-200 text-sm text-purple-900 flex items-center gap-1">
              {s.nome}
              <button
                onClick={() => setSelecionados(prev => prev.filter(x => x.funccodi !== s.funccodi))}
                className="p-0.5 rounded-full hover:bg-purple-100"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <input
          value={busca}
          onChange={e => setBusca(e.target.value)}
          placeholder="Buscar colaborador por nome (mín. 2 letras)..."
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400"
        />
        {buscando && <Loader2 className="w-4 h-4 animate-spin text-gray-400 absolute right-3 top-2.5" />}
      </div>
      {resultados.length > 0 && (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-auto">
          {resultados.map(c => {
            const ja = selecionados.some(s => s.funccodi === c.funccodi);
            return (
              <button
                key={c._id}
                onClick={() => adicionar(c)}
                disabled={ja}
                className="w-full text-left px-3 py-2 hover:bg-purple-50 text-sm disabled:opacity-50"
              >
                <div className="font-medium text-gray-800 truncate">{c.dados_publicos?.nome || '—'}</div>
                <div className="text-xs text-gray-500 truncate">
                  Mat. {c.dados_publicos?.matricula || c.funccodi}
                  {c.dados_publicos?.cargo_nome ? ` · ${c.dados_publicos.cargo_nome}` : ''}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {erro && (
        <div className="flex items-center gap-2 text-sm text-rose-700"><AlertCircle className="w-4 h-4" /> {erro}</div>
      )}

      <div className="flex justify-end">
        <button
          onClick={gerar}
          disabled={!selecionados.length || gerando}
          className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium flex items-center gap-2"
        >
          {gerando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
          Gerar {selecionados.length > 1 ? `${selecionados.length} links` : 'link'}
        </button>
      </div>
    </div>
  );
};

const Solicitacoes: React.FC<Props> = ({ user, onAbrirFicha }) => {
  const [filtro, setFiltro] = useState<Filtro>('');
  const [itens, setItens] = useState<DiscConvite[]>([]);
  const [resumo, setResumo] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [nova, setNova] = useState(false);
  const [gerados, setGerados] = useState<{ itens: DiscConvite[]; ignorados: DiscConviteIgnorado[] } | null>(null);
  const [aberto, setAberto] = useState<string | null>(null);
  const [regerando, setRegerando] = useState<number | null>(null);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const r = await atlasApi.discConvites(user, filtro ? { status: filtro } : {});
      setItens(r.itens || []);
      setResumo(r.resumo || {});
    } catch (e: any) {
      setErro(e?.message || 'Erro ao carregar as solicitações.');
    } finally {
      setLoading(false);
    }
  }, [user, filtro]);

  useEffect(() => { setLoading(true); carregar(); }, [carregar]);

  const gerarNovo = async (funccodi: number) => {
    setRegerando(funccodi);
    try {
      const r = await atlasApi.discSolicitar(user, [funccodi]);
      setGerados({ itens: r.itens, ignorados: r.ignorados });
      await carregar();
    } catch (e: any) {
      setErro(e?.message || 'Falha ao gerar o link.');
    } finally {
      setRegerando(null);
    }
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Solicitações de DISC</h2>
          <p className="text-sm text-gray-500">
            Gere o link, mande para o colaborador e acompanhe aqui. Quem responde não vê o resultado.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setLoading(true); carregar(); }}
            className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
            title="Atualizar"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {!nova && (
            <button
              onClick={() => { setNova(true); setGerados(null); }}
              className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Nova solicitação
            </button>
          )}
        </div>
      </div>

      {nova && (
        <NovaSolicitacao
          user={user}
          onFechar={() => setNova(false)}
          onGerado={(novos, ignorados) => {
            setNova(false);
            setGerados({ itens: novos, ignorados });
            setFiltro('');
            carregar();
          }}
        />
      )}

      {gerados && (
        <div className="bg-white rounded-2xl border border-emerald-200 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">
              {gerados.itens.length === 1 ? 'Link gerado' : `${gerados.itens.length} links gerados`}. Agora é só mandar.
            </h3>
            <button onClick={() => setGerados(null)} className="p-1 rounded-md text-gray-400 hover:bg-gray-100"><X className="w-4 h-4" /></button>
          </div>
          {gerados.itens.map(c => (
            <div key={c.id} className="border-t border-gray-100 pt-3 first:border-0 first:pt-0 space-y-2">
              <div className="text-sm font-medium text-gray-800">
                {c.nome} {c.cargo && <span className="text-gray-400 font-normal">· {c.cargo}</span>}
              </div>
              <LinkConvite user={user} convite={c} onAtualizado={carregar} compacto />
            </div>
          ))}
          {gerados.ignorados.length > 0 && (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Sem link: {gerados.ignorados.map(i => `${i.nome || `#${i.funccodi}`} (${i.motivo})`).join(', ')}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {FILTROS.map(f => (
          <button
            key={f.id || 'todas'}
            onClick={() => setFiltro(f.id)}
            className={`px-3 py-1.5 rounded-full text-sm border transition ${
              filtro === f.id ? 'bg-purple-600 border-purple-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-purple-300'
            }`}
          >
            {f.label}
            {f.id && resumo[f.id] !== undefined && <span className="ml-1.5 opacity-75">{resumo[f.id]}</span>}
          </button>
        ))}
      </div>

      {erro && <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-700">{erro}</div>}

      {loading ? (
        <div className="p-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : itens.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-sm text-gray-500">
          Nenhuma solicitação {filtro ? 'neste filtro' : 'ainda'}.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 divide-y divide-gray-100">
          {itens.map(c => {
            const expandido = aberto === c.id && c.status === 'pendente';
            return (
              <div key={c.id} className="p-4 space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[12rem]">
                    <div className="font-medium text-gray-900">{c.nome || `#${c.funccodi}`}</div>
                    <div className="text-xs text-gray-500">
                      {c.cargo ? `${c.cargo} · ` : ''}pedido em {dataHora(c.criado_em)}
                      {c.status === 'respondido' && ` · respondido em ${dataHora(c.respondido_em)}`}
                      {c.status === 'pendente' && ` · vale até ${dataHora(c.expira_em)}`}
                      {c.status === 'pendente' && c.acessado_em && ' · já abriu o link'}
                    </div>
                  </div>
                  <BadgeConvite status={c.status} />
                  {c.status === 'pendente' && (
                    <button
                      onClick={() => setAberto(expandido ? null : c.id)}
                      className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                    >
                      Link {expandido ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  )}
                  {(c.status === 'expirado' || c.status === 'cancelado') && (
                    <button
                      onClick={() => gerarNovo(c.funccodi)}
                      disabled={regerando === c.funccodi}
                      className="px-3 py-1.5 rounded-lg border border-purple-200 text-sm text-purple-700 hover:bg-purple-50 flex items-center gap-1 disabled:opacity-50"
                    >
                      {regerando === c.funccodi ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Gerar novo link
                    </button>
                  )}
                  {c.status === 'respondido' && (
                    <button
                      onClick={() => onAbrirFicha(c.funccodi)}
                      className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                    >
                      Ver perfil <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {expandido && <LinkConvite user={user} convite={c} onAtualizado={carregar} compacto />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Solicitacoes;
